import React from 'react'
import { Settings, Briefcase, Trophy } from 'lucide-react'
import { SurfboardIcon, WaveIcon, BeachIcon } from '@/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import packageJson from '../../../package.json'
import { useGameplayContext } from '@/contexts/gameplayContext'
import { QuestEnum } from '@/types'
import { NavLink } from 'react-router-dom'
import { getTotalUnworkedJobsCount, getUnpurchasedUpgradesCount, getCompletableQuestsCount } from '@/utils'

const version = packageJson.version
interface SidebarProps {
  isSidebarOpen: boolean
  setIsSidebarOpen: (isOpen: boolean) => void
}

const navItems = [
  { name: 'Main', icon: SurfboardIcon, path: '/main', emoji: '🏄‍♂️' },
  { name: 'Surf', icon: WaveIcon, path: '/surf', emoji: '🌊' },
  { name: 'Quests', icon: BeachIcon, path: '/quests', emoji: '🎯' },
  { name: 'Upgrades', icon: SurfboardIcon, path: '/upgrades', emoji: '🛍️' },
  { name: 'Skills', icon: Trophy, path: '/skills', emoji: '🏆' },
  { name: 'Jobs', icon: Briefcase, path: '/jobs', emoji: '💵' },
  { name: 'Locations', icon: BeachIcon, path: '/locations', emoji: '🌍', unlockQuest: 'the_mediterranean_farewell' },
  { name: 'Statistics', icon: WaveIcon, path: '/statistics', emoji: '📊' },
  { name: 'Story', icon: WaveIcon, path: '/story', emoji: '📖' },
  { name: 'Competitions', icon: Trophy, path: '/competitions', emoji: '🏅', unlockQuest: 'the_mediterranean_farewell' }, //'a_cold_reality'
  { name: 'Settings', icon: Settings, path: '/settings', emoji: '⚙️' }
]

export const Sidebar: React.FC<SidebarProps> = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { gameData } = useGameplayContext()
  const handleNavigation = (path: string) => {
    navigate(path)
    setIsSidebarOpen(false)
  }
  const unlockedNavItems = navItems.filter(
    (item) => !item.unlockQuest || gameData.completedQuests.includes(item.unlockQuest as QuestEnum)
  )

  // Get the count of unlocked but unworked jobs
  const unworkedJobsCount = getTotalUnworkedJobsCount(gameData)

  // Get the count of unpurchased upgrades
  const unpurchasedUpgradesCount = getUnpurchasedUpgradesCount(gameData)

  // Get the count of completable quests
  const completableQuestsCount = getCompletableQuestsCount(gameData)

  const navLinkStyles = ({ isActive }: { isActive: boolean }) =>
    `flex items-center space-x-2 w-full p-4 hover:bg-blue-700/80 transition-colors ${isActive ? 'bg-blue-700/80' : ''}`

  // Helper function to render a navigation button with optional badge
  const renderNavButton = (item: (typeof navItems)[0], isActive: boolean) => (
    <button key={item.path} className={navLinkStyles({ isActive })} onClick={() => handleNavigation(item.path)}>
      <span className="text-2xl">{item.emoji}</span>
      <div className="flex items-center font-medium">
        <span>{item.name}</span>
        {item.name === 'Jobs' && unworkedJobsCount > 0 && (
          <span className="ml-2 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center text-xs">
            {unworkedJobsCount}
          </span>
        )}
        {item.name === 'Upgrades' && unpurchasedUpgradesCount > 0 && (
          <span className="ml-2 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center text-xs">
            {unpurchasedUpgradesCount}
          </span>
        )}
        {item.name === 'Quests' && completableQuestsCount > 0 && (
          <span className="ml-2 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center text-xs">
            {completableQuestsCount}
          </span>
        )}
      </div>
    </button>
  )

  return (
    <>
      {/* Mobile Sidebar */}
      <div
        className={`lg:hidden fixed inset-0 z-50 bg-black bg-opacity-50 transition-opacity duration-300 ${
          isSidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsSidebarOpen(false)}
      >
        <nav
          className={`bg-gradient-to-b from-blue-600/70 to-blue-800/70 backdrop-blur-sm text-white w-64 h-full transform transition-transform duration-300 font-lexend relative overflow-y-auto ${
            isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="pb-12">
            {unlockedNavItems.map((item) => renderNavButton(item, location.pathname === item.path))}
          </div>
          <div className="absolute bottom-2 left-3 text-xs text-white/40">v{version}</div>
        </nav>
      </div>

      {/* Desktop Sidebar */}
      <nav className="hidden lg:block bg-gradient-to-b from-blue-600/70 to-blue-800/70 backdrop-blur-sm text-white w-64 flex-shrink-0 shadow-lg z-10 relative font-lexend h-screen overflow-y-auto">
        <div className="pb-12">
          {unlockedNavItems.map((item) => renderNavButton(item, location.pathname === item.path))}
          <div className="px-4 text-xs text-white/60">v{version}</div>
        </div>
      </nav>
    </>
  )
}
